import { createContext, useContext, useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

/**
 * Explorer do shell.
 *
 * No protótipo o explorer nao pertence a tela: ele e irmao do rail e do
 * `<main>` (`docs/design-slices/_shell.html:44-45,509`) e so troca de lista
 * conforme o modulo ativo. Quem controla busca, chips, botao criar e filtros e
 * o shell; a tela apenas LE esse estado e devolve a lista.
 *
 * Por isso o estado mora aqui e desce por contexto — o caminho inverso
 * (a tela empurrar a lista para cima a cada render) provocaria re-render
 * infinito. A lista em si viaja por portal, para o corpo rolavel do painel.
 */

export type ExplorerView = 'list' | 'tree'

/**
 * Item do mini-rail que o modulo publica para quando o painel esta recolhido.
 *
 * O shell desenha uma segunda barra vertical estreita com estes itens (so a
 * imagem/sigla); clicar troca a selecao sem precisar reabrir o painel. Usado
 * hoje pelas Aplicacoes (pedido 2026-08-26).
 */
export type ExplorerRailItem = {
  id: string
  label: string
  /** O que aparece no botao: imagem, icone ou a sigla do nome. */
  content: ReactNode
  active: boolean
  onClick: () => void
}

type ExplorerState = {
  /** Texto digitado na busca do explorer. */
  search: string
  /** Chip de filtro rapido selecionado (o primeiro do modulo, por padrao). */
  chip: string
  /** Lista ou arvore — so os modulos que tem as duas visoes usam. */
  view: ExplorerView
  /** Container do corpo rolavel; a tela renderiza a lista aqui por portal. */
  body: HTMLElement | null
  /**
   * Vaga no cabecalho, ao lado do "+": botoes extras da tela (ex.: a
   * engrenagem de personalizacao da Comunicacao, 2026-09-14).
   */
  acoes: HTMLElement | null
  /**
   * Limpa busca e chip, voltando ao primeiro chip do modulo.
   *
   * Existe para o CTA do estado vazio ("nenhum resultado") poder desfazer o
   * filtro — sem isso a tela so conseguiria dizer ao usuario onde clicar.
   */
  clearFilters: () => void
  /** Publica o resumo do rodape (ex.: "18 Itens de Trabalho · ..."). */
  setFooter: (texto: string | null) => void
  /** Registra o que o botao "+" do explorer faz nesta tela. */
  setCreateHandler: (acao: (() => void) | null) => void
  /** Registra se a tela oferece as duas visoes (mostra o alternador). */
  setHasTreeView: (tem: boolean) => void
  /** Itens do mini-rail exibido quando o painel esta recolhido. */
  railItems: ExplorerRailItem[]
  /** O modulo publica (ou limpa) os itens do mini-rail. */
  setRailItems: (itens: ExplorerRailItem[]) => void
}

const Ctx = createContext<ExplorerState | null>(null)

export const ExplorerProvider = Ctx.Provider

/**
 * Estado do explorer para a tela ativa.
 *
 * Fora do shell (testes, storybook) devolve um estado neutro em vez de
 * quebrar — assim uma tela pode ser montada isolada.
 */
export function useExplorer(): ExplorerState {
  return (
    useContext(Ctx) ?? {
      search: '',
      chip: '',
      view: 'list',
      body: null,
      acoes: null,
      clearFilters: () => {},
      setFooter: () => {},
      setCreateHandler: () => {},
      setHasTreeView: () => {},
      railItems: [],
      setRailItems: () => {},
    }
  )
}

/**
 * Publica os itens do mini-rail (barra estreita quando o painel recolhe).
 *
 * Passe uma lista estavel (memoizada) — cada mudanca re-registra. Ao desmontar
 * a tela, o rail e limpo automaticamente.
 */
export function useExplorerRail(items: ExplorerRailItem[]) {
  const { setRailItems } = useExplorer()
  useEffect(() => {
    setRailItems(items)
    return () => setRailItems([])
  }, [items, setRailItems])
}

/** Renderiza a lista da tela dentro do corpo rolavel do explorer. */
export function ExplorerBody({ children }: { children: ReactNode }) {
  const { body } = useExplorer()
  if (!body) return null
  return createPortal(children, body)
}

/** Renderiza botoes extras da tela no cabecalho do explorer, ao lado do "+". */
export function ExplorerHeaderActions({ children }: { children: ReactNode }) {
  const { acoes } = useExplorer()
  if (!acoes) return null
  return createPortal(children, acoes)
}

/**
 * Acucar para a tela declarar o rodape, o botao criar e o alternador de visao.
 *
 * `acaoCriar` entra por ref para nao exigir `useCallback` na tela.
 */
export function useExplorerConfig(options: {
  footer?: string | null
  onCreate?: () => void
  hasTreeView?: boolean
}) {
  const { setFooter, setCreateHandler, setHasTreeView } = useExplorer()
  const { footer = null, onCreate, hasTreeView = false } = options

  const criarRef = useRef(onCreate)
  criarRef.current = onCreate
  // A tela pode ligar e desligar o botão (a Comunicação só oferece "Nova
  // conversa" na aba Tudo): sem isto na dependência, o efeito rodava uma vez
  // só e o botão ficava congelado no estado da primeira renderização.
  const temCriar = onCreate !== undefined

  useEffect(() => {
    setFooter(footer)
    return () => setFooter(null)
  }, [footer, setFooter])

  useEffect(() => {
    setCreateHandler(criarRef.current ? () => criarRef.current?.() : null)
    return () => setCreateHandler(null)
  }, [setCreateHandler, temCriar])

  useEffect(() => {
    setHasTreeView(hasTreeView)
    return () => setHasTreeView(false)
  }, [hasTreeView, setHasTreeView])
}

/** Largura inicial do painel — protótipo `_shell.html:2918` (`paneW: 372`). */
export const PANE_LARGURA_PADRAO = 372
/** Limites do arraste — protótipo `_shell.html:2070-2086`. */
export const PANE_MIN = 248
export const PANE_MAX = 560
/** Abaixo disto o arraste recolhe o painel em vez de encolher. */
export const PANE_COLAPSA_ABAIXO_DE = 130

const CHAVE_LARGURA = 'ob-expl-w'
const CHAVE_COLAPSO = 'ob-expl-col'

/**
 * Largura e estado recolhido do explorer, persistidos entre navegacoes.
 *
 * O protótipo guarda isso em localStorage (`_shell.html:2015-2019, 2030-2033`);
 * sem isso o painel volta ao padrao a cada troca de modulo.
 */
export function usePaneSize() {
  const [width, setWidth] = useState(() => {
    const salvo = Number(localStorage.getItem(CHAVE_LARGURA))
    return Number.isFinite(salvo) && salvo >= PANE_MIN && salvo <= PANE_MAX ? salvo : PANE_LARGURA_PADRAO
  })
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(CHAVE_COLAPSO) === '1')

  useEffect(() => {
    localStorage.setItem(CHAVE_LARGURA, String(width))
  }, [width])
  useEffect(() => {
    localStorage.setItem(CHAVE_COLAPSO, collapsed ? '1' : '0')
  }, [collapsed])

  return { width, setWidth, collapsed, setCollapsed }
}
